import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ArrowLeft } from "@phosphor-icons/react";

export default function RegisterPage() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 6) {
            setError("Password must be at least 6 characters.");
            return;
        }
        if (password !== confirm) {
            setError("Passwords do not match.");
            return;
        }
        setLoading(true);
        setError("");
        const res = await register(email, password, name);
        setLoading(false);
        if (res.ok) {
            toast.success(`Welcome to TrojanTrails, ${name}!`);
            navigate("/feed");
        } else {
            setError(res.error);
        }
    };

    return (
        <div className="min-h-screen bg-[#FAFAFA] flex" data-testid="register-page">
            <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-[#990000]">
                <div className="absolute inset-0 opacity-20"
                     style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #FFCC00 0%, transparent 40%), radial-gradient(circle at 80% 70%, #FFCC00 0%, transparent 35%)" }} />
                <div className="relative z-10 flex flex-col justify-between p-12 text-white">
                    <Link to="/" className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-lg bg-[#FFCC00] flex items-center justify-center">
                            <span className="text-[#5C0000] font-heading font-extrabold">T</span>
                        </div>
                        <span className="font-heading font-bold tracking-tight">TrojanTrails</span>
                    </Link>
                    <div>
                        <div className="text-xs uppercase tracking-[0.2em] text-[#FFCC00] font-bold mb-3">JOIN USC × INDIA</div>
                        <h2 className="font-heading text-4xl font-bold leading-tight mb-3">
                            Be part of the<br /> Trojan travel circle.
                        </h2>
                        <p className="text-white/70 max-w-sm leading-relaxed">
                            Share your favourite India spots and discover trusted recs from your USC family.
                        </p>
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
                        Fight On · Travel On
                    </div>
                </div>
            </div>

            <div className="flex-1 flex flex-col">
                <div className="p-6">
                    <Link to="/" data-testid="register-back-link" className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-[#990000]">
                        <ArrowLeft size={16} /> Back to home
                    </Link>
                </div>
                <div className="flex-1 flex items-center justify-center px-6 py-12">
                    <div className="w-full max-w-md">
                        <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#990000] mb-2">Create account</div>
                        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[#0A0A0A] mb-2">
                            Join TrojanTrails
                        </h1>
                        <p className="text-stone-500 mb-8">
                            Already have an account?{" "}
                            <Link to="/login" data-testid="register-to-login-link" className="text-[#990000] font-semibold hover:underline">Sign in</Link>
                        </p>

                        <form onSubmit={submit} className="space-y-5">
                            <div>
                                <Label htmlFor="name" className="text-xs uppercase tracking-[0.15em] font-bold text-stone-600">Full Name</Label>
                                <Input id="name" type="text" data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Tommy Trojan" className="mt-2 h-11 border-stone-300 focus-visible:ring-[#990000] focus-visible:border-[#990000]" required />
                            </div>
                            <div>
                                <Label htmlFor="email" className="text-xs uppercase tracking-[0.15em] font-bold text-stone-600">Email</Label>
                                <Input id="email" type="email" data-testid="register-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@usc.edu" className="mt-2 h-11 border-stone-300 focus-visible:ring-[#990000] focus-visible:border-[#990000]" required />
                            </div>
                            <div>
                                <Label htmlFor="password" className="text-xs uppercase tracking-[0.15em] font-bold text-stone-600">Password</Label>
                                <Input id="password" type="password" data-testid="register-password-input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" className="mt-2 h-11 border-stone-300 focus-visible:ring-[#990000] focus-visible:border-[#990000]" required minLength={6} />
                            </div>
                            <div>
                                <Label htmlFor="confirm" className="text-xs uppercase tracking-[0.15em] font-bold text-stone-600">Confirm Password</Label>
                                <Input
                                    id="confirm"
                                    type="password"
                                    data-testid="register-confirm-input"
                                    value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                    placeholder="Re-enter password"
                                    className={`mt-2 h-11 focus-visible:ring-[#990000] focus-visible:border-[#990000] ${
                                        confirm && password !== confirm
                                            ? "border-[#990000]"
                                            : "border-stone-300"
                                    }`}
                                    required
                                    minLength={6}
                                />
                                {confirm && password !== confirm && (
                                    <div data-testid="register-confirm-mismatch" className="text-xs text-[#990000] mt-1.5">
                                        Passwords don't match yet.
                                    </div>
                                )}
                            </div>

                            {error && (
                                <div data-testid="register-error" className="text-sm text-[#990000] bg-[#990000]/5 border border-[#990000]/20 rounded-lg px-3 py-2">{error}</div>
                            )}

                            <Button type="submit" data-testid="register-submit-btn" disabled={loading} className="w-full h-11 bg-[#990000] hover:bg-[#7A0000] text-white font-medium">
                                {loading ? "Creating account…" : "Create account"}
                            </Button>
                        </form>

                        <p className="text-xs text-stone-400 mt-6 text-center">
                            By joining, you agree to be respectful & helpful in the community.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
